/**
 * Popup-safe open for presigned download URLs (CC-300 / CC-302 / CC-329).
 *
 * The one shared implementation of the pattern — extracted after the same
 * spec bug shipped five times: passing the `noopener` FEATURE to
 * `window.open` makes it return `null` by spec, so a "fallback" assignment
 * to `window.location.href` after the await silently navigated the SPA tab
 * away. Correct sequence:
 *
 *   1. Open the tab SYNCHRONOUSLY on the user gesture (no `noopener`
 *      feature — the opener link is severed manually instead).
 *   2. `null` return = the browser genuinely blocked the popup → tell the
 *      user; never fall back to the app tab's location.
 *   3. Resolve the short-lived presigned URL, then `location.replace` it
 *      into the popup.
 *   4. Any failure closes the popup and hands the raw error to the caller —
 *      error CLASSIFICATION (endpoint-specific absence codes vs real
 *      failures) stays per-surface.
 */
import { toSameOriginDevStorage } from "../storage/presignedUpload.js";

export interface PresignedDownloadHandlers {
  /**
   * The URL could not be resolved or the bytes could not be fetched.
   * `undefined` means the read succeeded but carried no URL — the same
   * "not available to this caller" signal `onFailure` carries below.
   */
  onFailure: (error: unknown) => void;
}

export interface PresignedOpenHandlers {
  /** The browser blocked the popup — surface a localized warning toast. */
  onPopupBlocked: () => void;
  /**
   * The URL could not be resolved: `resolveUrl` threw (typically an
   * `ApiError`) or returned no URL (`undefined` is passed then). The caller
   * classifies absence codes vs honest errors and toasts accordingly.
   */
  onFailure: (error: unknown) => void;
}

/**
 * Upper bound on URL resolution — a never-settling resolver must not leave
 * a blank tab open forever. Presigns settle in single-digit seconds.
 */
const RESOLVE_TIMEOUT_MS = 30_000;

/**
 * The server's own filename, per RFC 6266 — it authors the name (report title,
 * period, extension derived from the same value as the stored content type), so
 * it wins over anything we could assemble. The extended form is preferred: it
 * is the one that survives an Arabic title.
 *
 * Returns undefined when the header is unreadable, which is the normal case
 * cross-origin unless the bucket lists `Content-Disposition` in
 * `Access-Control-Expose-Headers`. Callers fall back rather than fail.
 */
export function fileNameFromDisposition(header: string | null): string | undefined {
  if (header === null || header.length === 0) return undefined;
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
  if (extended?.[1] !== undefined) {
    try {
      const decoded = decodeURIComponent(extended[1].trim());
      if (decoded.length > 0) return decoded;
    } catch {
      // Malformed percent-encoding — fall through to the ASCII form.
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i.exec(header);
  const raw = (plain?.[1] ?? plain?.[2])?.trim();
  return raw !== undefined && raw.length > 0 ? raw : undefined;
}

/** Last path segment, which is what the storage key ends with. */
function fileNameFromUrl(url: string): string {
  try {
    const path = new URL(url, "http://localhost").pathname;
    const last = path.split("/").filter(Boolean).pop();
    return last !== undefined && last.length > 0 ? decodeURIComponent(last) : "download";
  } catch {
    return "download";
  }
}

/**
 * Save a presigned artifact as a FILE, without navigating anything.
 *
 * The tab-opening sibling below is right for something a user wants to LOOK at.
 * It is wrong for a Download button. Originally that was because the presigned
 * URLs carried no `Content-Disposition` at all and exports are stored as
 * `text/csv`, so a browser sent to the URL RENDERED the rows in a tab instead of
 * saving them — a blank-looking page for a small export, and never a file.
 *
 * The server declares the disposition now (CC-442), so a navigation would
 * download. Fetching still wins: it never risks taking the SPA with it if a
 * surface is ever served inline, it leaves no empty tab behind, and it works
 * the same in every environment regardless of which ones have the header. We
 * read the server's filename off the response and prefer it — see
 * `fileNameFromDisposition`.
 *
 * The fetch carries NO credentials and NO added headers — same rule as the
 * presigned PUT. A signed URL already carries its own authority, and any extra
 * header would turn this into a preflighted request the bucket does not answer.
 */
export async function downloadPresigned(
  resolveUrl: () => Promise<string | null | undefined>,
  handlers: PresignedDownloadHandlers,
  /**
   * `fileName` may be a thunk: the name usually comes from the same read that
   * mints the URL, so it is not known when the download is requested. Falls
   * back to the storage key's last segment.
   */
  opts: { fileName?: string | (() => string | undefined) } = {},
): Promise<void> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    handlers.onFailure(new Error("downloadPresigned requires a browser document"));
    return;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  let objectUrl: string | undefined;
  try {
    const url = await Promise.race([
      resolveUrl(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error("downloadPresigned: timed out resolving the download URL"));
        }, RESOLVE_TIMEOUT_MS);
      }),
    ]);
    if (typeof url !== "string" || url.length === 0) {
      handlers.onFailure(undefined);
      return;
    }
    const res = await fetch(toSameOriginDevStorage(url), { credentials: "omit", mode: "cors" });
    if (!res.ok) {
      throw new Error(`presigned download failed with status ${res.status}`);
    }
    const blob = await res.blob();
    objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    // Precedence: the server's name, then the caller's, then the storage key.
    const fallback = typeof opts.fileName === "function" ? opts.fileName() : opts.fileName;
    const served = fileNameFromDisposition(res.headers.get("content-disposition"));
    anchor.download =
      served ?? (fallback !== undefined && fallback.length > 0 ? fallback : fileNameFromUrl(url));
    anchor.rel = "noopener";
    anchor.style.display = "none";
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } catch (err) {
    handlers.onFailure(err === undefined ? new Error("presigned download failed") : err);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    // Revoked on a delay, not immediately: the click hands the blob to the
    // browser's download machinery asynchronously, and pulling the URL out from
    // under it can cancel the save.
    if (objectUrl !== undefined) {
      const toRevoke = objectUrl;
      setTimeout(() => URL.revokeObjectURL(toRevoke), 10_000);
    }
  }
}

export async function openPresignedInNewTab(
  resolveUrl: () => Promise<string | null | undefined>,
  handlers: PresignedOpenHandlers,
): Promise<void> {
  // SSR / non-DOM test-environment guard.
  if (typeof window === "undefined") {
    handlers.onFailure(new Error("openPresignedInNewTab requires a browser window"));
    return;
  }
  const win = window.open("", "_blank");
  if (win === null) {
    handlers.onPopupBlocked();
    return;
  }
  win.opener = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const url = await Promise.race([
      resolveUrl(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error("openPresignedInNewTab: timed out resolving the download URL"));
        }, RESOLVE_TIMEOUT_MS);
      }),
    ]);
    if (typeof url === "string" && url.length > 0) {
      win.location.replace(url);
      return;
    }
    win.close();
    handlers.onFailure(undefined);
  } catch (err) {
    win.close();
    // `undefined` is reserved for the resolved-but-no-URL outcome above; a
    // thrown `undefined` (Promise.reject(undefined)) must still read as a
    // real failure, so normalize it into an Error before handing it over.
    handlers.onFailure(err === undefined ? new Error("presigned URL resolution failed") : err);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
